import { useCallback, useEffect, useRef } from 'react'
import {
  TASK_MENU_COMMAND_CHANNEL,
  TASK_MENU_STATE_CHANNEL,
  type TaskCommand,
  type TaskMenuState,
} from '@/src/shared/taskMenu'
import type { Task } from '@/src/renderer/lib/db/models'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import type { TaskMenuTarget } from '../components/TaskMenu/TaskMenu.types'

/** Was das Menü über die gemeinte Aufgabe wissen muss — den **Anker** (`anchorTaskId`). */
export interface AppTaskMenuAnchor {
  id: string
  completed: boolean
  resting: boolean
  favorite: boolean
  projectId: string | null
  subtask?: boolean
  /** Unter einer wiederkehrenden Aufgabe hat sie keinen eigenen Rhythmus (#205). */
  parentRecurring?: boolean
}

type AnchorFields = Pick<Task, 'id' | 'status' | 'pausedAt' | 'favoriteOrder' | 'projectId' | 'parentTaskId' | 'recurrence'>

/** `tasks` dient nur der Frage, ob sich eine Ebene darüber wiederholt. */
export function menuAnchorOf(task: AnchorFields | null | undefined, tasks?: readonly AnchorFields[]): AppTaskMenuAnchor | null {
  if (!task) return null
  let parentRecurring = false
  for (let parentId = task.parentTaskId, hops = 0; parentId && tasks && hops < 10; hops++) {
    const parent = tasks.find(entry => entry.id === parentId)
    if (!parent) break
    if (parent.recurrence) { parentRecurring = true; break }
    parentId = parent.parentTaskId
  }
  return {
    id: task.id,
    completed: task.status === 'completed',
    resting: task.pausedAt != null,
    favorite: task.favoriteOrder != null,
    projectId: task.projectId,
    subtask: task.parentTaskId != null,
    ...(parentRecurring ? { parentRecurring } : {}),
  }
}

/**
 * **„Im Projekt zeigen“** — derselbe Sprung wie aus der Suche und dem Toast (`open-task`): Er
 * wechselt bei Bedarf den Lebensbereich und holt die Aufgabe dort ins Bild.
 */
export function useShowTaskInProject(): (taskId: string) => void {
  const source = useDataSource()
  return useCallback((taskId: string) => {
    void source.getTask(taskId).then(task => {
      if (task && !task.deletedAt) dispatchAppEvent('open-task', { task })
    })
  }, [source])
}

/** Was die Seite kann. Fehlt ein Griff, bleibt sein Eintrag grau. */
export interface AppTaskMenuHandlers {
  /** Die Picker an der Zeile — derselbe Weg wie W, ⇧D & Co. */
  onMenuShortcut?: (taskId: string, target: TaskMenuTarget) => void
  /** Erledigen bzw. Wieder öffnen — die ganze Auswahl, wie die Leertaste. */
  onComplete?: () => void
  onFocus?: (taskId: string) => void
  onRest?: (taskId: string) => void
  onFavorite?: (taskId: string) => void
  onToggleDetails?: () => void
  onShowInProject?: (taskId: string) => void
  onDuplicate?: () => void
  /** Die ganze Auswahl, wie ⌫. */
  onDelete?: () => void
}

export interface UseAppTaskMenuParams extends AppTaskMenuHandlers {
  /** Nur die aktive Seite meldet sich (TabStack hält die anderen montiert). */
  enabled: boolean
  anchor: AppTaskMenuAnchor | null
  multiSelected?: boolean
  detailsOpen?: boolean
  /** Das Projekt der Seite — „Im Projekt zeigen“ führte dort nirgends hin. */
  currentProjectId?: string | null
  /** Die Seite stellt „Verschieben …“ bereit (`MoveMenuContext`); es geht am Anker auf wie W & Co. */
  canMove?: boolean
  /** Nur diese Befehle können frei sein — im Einsortieren allein „Verschieben …“ (#267). */
  only?: readonly TaskCommand[]
}

const PICKERS: Partial<Record<TaskCommand, TaskMenuTarget>> = {
  when: 'start',
  deadline: 'deadline',
  duration: 'duration',
  recurrence: 'recurrence',
  tags: 'tags',
  attention: 'attention',
  reward: 'reward',
}

/**
 * Was frei ist. Für eine erledigte Aufgabe gilt, was ihr Rechtsklick-Menü zeigt (`kind: 'done'`):
 * Wieder öffnen, Favorit, Im Projekt zeigen, Löschen.
 */
export function appTaskMenuState(params: Omit<UseAppTaskMenuParams, 'enabled'>): TaskMenuState {
  const { anchor, multiSelected = false, detailsOpen = false, currentProjectId = null } = params
  const enabled: TaskCommand[] = []
  const add = (command: TaskCommand, when: unknown) => { if (when) enabled.push(command) }

  if (anchor) {
    const open = !anchor.completed
    for (const command of Object.keys(PICKERS) as TaskCommand[]) {
      add(command, open && params.onMenuShortcut && !(command === 'recurrence' && anchor.parentRecurring))
    }
    add('complete', params.onComplete)
    // Ein Fokus gilt genau EINER offenen, wachen Aufgabe (CONTEXT.md).
    add('focus', open && params.onFocus && !multiSelected && !anchor.resting)
    add('rest', open && params.onRest)
    add('favorite', params.onFavorite && !anchor.subtask)
    add('showInProject', params.onShowInProject && anchor.projectId && anchor.projectId !== currentProjectId)
    // Eine Unteraufgabe zieht mit ihrer Aufgabe; für sich hat sie „Umhängen“ (#205).
    add('move', open && params.canMove && params.onMenuShortcut && !anchor.subtask)
    add('duplicate', open && params.onDuplicate)
    add('delete', params.onDelete)
  }
  // Offene Details lassen sich auch ohne Auswahl schließen.
  add('details', params.onToggleDetails && (anchor || detailsOpen))

  return {
    enabled: params.only ? enabled.filter(command => params.only!.includes(command)) : enabled,
    completed: anchor?.completed ?? false,
    resting: anchor?.resting ?? false,
    favorite: anchor?.favorite ?? false,
    detailsOpen,
  }
}

/**
 * **Das Menü „Aufgaben“ in der Menüleiste** (#257, nur Electron): Die aktive Seite meldet, was
 * an ihrer Auswahl gerade geht, und führt aus, was dort gewählt wird. Ohne `window.api` (Web)
 * tut der Hook nichts.
 */
export function useAppTaskMenu(params: UseAppTaskMenuParams): void {
  const { enabled } = params
  const state = appTaskMenuState(params)
  const stateKey = JSON.stringify(state)

  const latestRef = useRef(params)
  latestRef.current = params

  /*
    Zwei Effekte, damit beim Wechsel der Auswahl nur EINE Nachricht geht (jede baut das Menü neu).
    Beim Tab-Wechsel räumt React erst alle alten Effekte ab und setzt dann die neuen — das `null`
    der alten Seite kommt so immer vor der Meldung der neuen.
  */
  useEffect(() => {
    if (!enabled || !window.api?.send) return
    window.api.send(TASK_MENU_STATE_CHANNEL, JSON.parse(stateKey) as TaskMenuState)
  }, [enabled, stateKey])

  useEffect(() => {
    if (!enabled || !window.api?.send) return
    return () => window.api?.send?.(TASK_MENU_STATE_CHANNEL, null)
  }, [enabled])

  useEffect(() => {
    if (!enabled || !window.api?.receive) return
    const unsubscribe = window.api.receive(TASK_MENU_COMMAND_CHANNEL, (command: TaskCommand) => {
      runTaskCommand(latestRef.current, command)
    })
    return () => { unsubscribe?.() }
  }, [enabled])
}

/** Führt einen Menübefehl aus — nur, was laut Meldung gerade frei ist. */
export function runTaskCommand(params: Omit<UseAppTaskMenuParams, 'enabled'>, command: TaskCommand): void {
  if (!appTaskMenuState(params).enabled.includes(command)) return
  const taskId = params.anchor?.id ?? ''
  const picker = PICKERS[command]
  if (picker) {
    params.onMenuShortcut?.(taskId, picker)
    return
  }
  switch (command) {
    case 'complete': params.onComplete?.(); break
    case 'focus': params.onFocus?.(taskId); break
    case 'rest': params.onRest?.(taskId); break
    case 'favorite': params.onFavorite?.(taskId); break
    case 'details': params.onToggleDetails?.(); break
    case 'showInProject': params.onShowInProject?.(taskId); break
    case 'move': params.onMenuShortcut?.(taskId, 'move'); break
    case 'duplicate': params.onDuplicate?.(); break
    case 'delete': params.onDelete?.(); break
  }
}
